/**
 * Data gap detection and handling utilities
 * 
 * This module provides functions to detect gaps in time series data and
 * handle them appropriately by either interpolating missing points or
 * marking them for visual indication (dashed lines).
 * 
 * Requirements: 9.5
 */

/**
 * Calculate the expected interval between data points based on time range
 * 
 * @param {string} timeRange - The time range ('24h', '7d', '30d', '1y')
 * @returns {number} Expected interval in milliseconds
 */
const getExpectedInterval = (timeRange) => {
  switch (timeRange) {
    case "24h":
      return 15 * 60 * 1000 // 15 minutes in milliseconds
    case "7d":
      return 2 * 60 * 60 * 1000 // 2 hours in milliseconds
    case "30d":
      return 6 * 60 * 60 * 1000 // 6 hours in milliseconds
    case "1y":
      return 3 * 24 * 60 * 60 * 1000 // 3 days in milliseconds
    default:
      return 24 * 60 * 60 * 1000 // Default to 1 day
  }
}

/**
 * Detect gaps in time series data
 * 
 * A gap is detected when the time difference between consecutive data points
 * exceeds 1.5x the expected interval for the given time range.
 * 
 * @param {Array} data - Array of data points with timestamp and value
 * @param {string} timeRange - The time range context
 * @returns {Array} Array of gap objects with startIndex, endIndex, and duration
 */
export const detectGaps = (data, timeRange) => {
  if (!data || data.length < 2) {
    return []
  }

  const expectedInterval = getExpectedInterval(timeRange)
  const gapThreshold = expectedInterval * 1.5 // 50% tolerance
  const gaps = []

  for (let i = 1; i < data.length; i++) {
    const prevTimestamp = data[i - 1].timestamp
    const currentTimestamp = data[i].timestamp
    const timeDiff = currentTimestamp - prevTimestamp

    if (timeDiff > gapThreshold) {
      gaps.push({
        startIndex: i - 1,
        endIndex: i,
        duration: timeDiff,
        startTimestamp: prevTimestamp,
        endTimestamp: currentTimestamp,
      })
    }
  }

  return gaps
}

/**
 * Interpolate missing data points in gaps
 * 
 * Uses linear interpolation to fill gaps with estimated values.
 * Adds a 'interpolated' flag to generated points for visual indication.
 * 
 * @param {Array} data - Array of data points with timestamp and value
 * @param {string} timeRange - The time range context
 * @returns {Array} Array with interpolated points added
 */
export const interpolateGaps = (data, timeRange) => {
  if (!data || data.length < 2) {
    return data
  }

  const gaps = detectGaps(data, timeRange)
  
  if (gaps.length === 0) {
    return data
  }

  const expectedInterval = getExpectedInterval(timeRange)
  const result = []

  let currentGapIndex = 0
  
  for (let i = 0; i < data.length; i++) {
    result.push(data[i])

    // Check if we're at the start of a gap
    if (currentGapIndex < gaps.length && i === gaps[currentGapIndex].startIndex) {
      const gap = gaps[currentGapIndex]
      const startPoint = data[gap.startIndex]
      const endPoint = data[gap.endIndex]
      
      // Calculate number of points to interpolate
      const numPoints = Math.floor(gap.duration / expectedInterval) - 1
      
      // Only interpolate if the gap is not too large (max 10 points)
      if (numPoints > 0 && numPoints <= 10) {
        const valueStep = (endPoint.value - startPoint.value) / (numPoints + 1)
        const timeStep = gap.duration / (numPoints + 1)

        for (let j = 1; j <= numPoints; j++) {
          result.push({
            timestamp: startPoint.timestamp + Math.round(timeStep * j),
            value: startPoint.value + (valueStep * j),
            interpolated: true, // Flag for visual indication
          })
        }
      }

      currentGapIndex++
    }
  }

  return result
}

/**
 * Add gap markers to data for dashed line visualization
 * 
 * Instead of interpolating, this function adds null values at gap boundaries
 * which causes Recharts to render a dashed or broken line.
 * 
 * @param {Array} data - Array of data points with timestamp and value
 * @param {string} timeRange - The time range context
 * @returns {Array} Array with gap markers added
 */
export const addGapMarkers = (data, timeRange) => {
  if (!data || data.length < 2) {
    return data
  }

  const gaps = detectGaps(data, timeRange)
  
  if (gaps.length === 0) {
    return data
  }

  const result = []

  for (let i = 0; i < data.length; i++) {
    result.push(data[i])

    // Check if there's a gap after this point
    const gap = gaps.find(g => g.startIndex === i)
    if (gap) {
      // Add a marker point with null value to create a visual break
      result.push({
        timestamp: data[i].timestamp + 1,
        value: null,
        gap: true,
      })
    }
  }

  return result
}

/**
 * Check if data has any gaps
 * 
 * @param {Array} data - Array of data points
 * @param {string} timeRange - The time range context
 * @returns {boolean} True if gaps are detected
 */
export const hasGaps = (data, timeRange) => {
  const gaps = detectGaps(data, timeRange)
  return gaps.length > 0
}

/**
 * Get gap statistics for display
 * 
 * @param {Array} data - Array of data points
 * @param {string} timeRange - The time range context
 * @returns {Object} Statistics about gaps (count, totalDuration, etc.)
 */
export const getGapStatistics = (data, timeRange) => {
  const gaps = detectGaps(data, timeRange)
  
  if (gaps.length === 0) {
    return {
      count: 0,
      totalDuration: 0,
      maxDuration: 0,
    }
  }

  const totalDuration = gaps.reduce((sum, gap) => sum + gap.duration, 0)
  const maxDuration = Math.max(...gaps.map(gap => gap.duration))

  return {
    count: gaps.length,
    totalDuration,
    maxDuration,
    gaps,
  }
}




